import { motion } from 'framer-motion';
import { Check, Landmark, QrCode, CreditCard, Banknote } from 'lucide-react';
import { EASE, REDUCE } from './atoms.jsx';

/**
 * The product, drawn in CSS.
 *
 * No screenshots: a screenshot is out of date the day after it is taken, does
 * not follow the theme, and cannot be read by anyone using a screen reader.
 * These are the real components' proportions and tokens, at rest.
 */

/** The hosted checkout, which is the thing a merchant is actually buying. */
export function CheckoutMock() {
  const methods = [
    { icon: CreditCard, label: 'Card', note: 'Visa, Mastercard, Amex', on: true },
    { icon: QrCode, label: 'UPI', note: 'Pay from any UPI app' },
    { icon: Landmark, label: 'Bank transfer', note: 'We confirm when it lands' },
  ];

  return (
    <div
      className="w-full overflow-hidden rounded-2xl border"
      style={{ background: 'rgb(var(--surface))', borderColor: 'rgb(var(--line))', boxShadow: 'var(--shadow-lg)' }}
    >
      <div className="flex items-center justify-between border-b px-5 py-3.5" style={{ borderColor: 'rgb(var(--line-soft))' }}>
        <div className="flex items-center gap-2.5">
          <div className="h-6 w-6 rounded-md" style={{ background: 'rgb(var(--ink))' }} />
          <span className="text-[13px] font-medium text-ink">Bean There Coffee</span>
        </div>
        <span className="text-[11px] text-faint">Secure checkout</span>
      </div>

      <div className="px-5 py-5">
        <div className="text-[11px] uppercase tracking-[0.12em] text-faint">Total</div>
        <div className="tabular mt-1 text-[30px] font-semibold leading-none tracking-tight text-ink">₹1,840.00</div>

        <div className="mt-5 space-y-2">
          {methods.map((m) => (
            <div
              key={m.label}
              className="flex items-center gap-3 rounded-xl border px-3.5 py-3"
              style={{
                borderColor: m.on ? 'rgb(var(--ink))' : 'rgb(var(--line))',
                background: m.on ? 'rgb(var(--surface-2))' : 'transparent',
              }}
            >
              <span
                className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border"
                style={{ borderColor: 'rgb(var(--line))', background: 'rgb(var(--surface-2))' }}
              >
                <m.icon className="h-4 w-4 text-ink-soft" aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-medium text-ink">{m.label}</span>
                <span className="block text-[11px] text-muted">{m.note}</span>
              </span>
              {m.on && (
                <span className="grid h-4 w-4 place-items-center rounded-full" style={{ background: 'rgb(var(--ink))' }}>
                  <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} aria-hidden="true" />
                </span>
              )}
            </div>
          ))}
        </div>

        <div className="mt-5 h-10 rounded-lg" style={{ background: 'rgb(var(--ink))' }}>
          <div className="grid h-full place-items-center text-[13px] font-medium text-white">Pay ₹1,840.00</div>
        </div>

        {/*
          * Said on the page because it is the one claim that matters and the
          * one a merchant cannot verify for themselves: the card never reaches
          * our servers, it is tokenized in the browser (HC-4/HC-5).
          */}
        <p className="mt-3 text-center text-[11px] text-faint">Card details never touch our servers</p>
      </div>
    </div>
  );
}

/** A payment settling, as the dashboard shows it. */
export function ActivityMock() {
  const rows = [
    { tone: 'ok', label: 'Payment succeeded', meta: 'Charge approved · 412 ms', amount: '₹1,840.00' },
    { tone: 'wait', label: 'Customer chose UPI', meta: 'Awaiting your confirmation', amount: '₹499.00' },
    { tone: 'bad', label: 'Card declined', meta: 'insufficient_funds · attempt 1', amount: '₹2,300.00' },
    { tone: 'ok', label: 'Cash taken at the till', meta: 'Ravi · counter', amount: '₹250.00' },
  ];

  const dot = {
    ok: '#059669',
    wait: '#B45309',
    bad: '#DC2626',
  };

  return (
    <div
      className="w-full overflow-hidden rounded-2xl border"
      style={{ background: 'rgb(var(--surface))', borderColor: 'rgb(var(--line))', boxShadow: 'var(--shadow-md)' }}
    >
      <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: 'rgb(var(--line-soft))' }}>
        <span className="text-[12px] font-medium text-ink">Activity</span>
        <span className="text-[11px] text-faint">live</span>
      </div>
      <div>
        {rows.map((r, i) => (
          <motion.div
            key={r.label}
            initial={REDUCE ? false : { opacity: 0, y: 6 }}
            whileInView={REDUCE ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.08, ease: EASE }}
            className="flex items-center gap-3 border-b px-4 py-3 last:border-0"
            style={{ borderColor: 'rgb(var(--line-soft))' }}
          >
            <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: dot[r.tone] }} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12.5px] text-ink">{r.label}</span>
              <span className="block truncate text-[11px] text-muted">{r.meta}</span>
            </span>
            <span className="tabular shrink-0 text-[12px] font-medium text-ink">{r.amount}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/** The till, for the half of the pitch that happens in a shop. */
export function TillMock() {
  return (
    <div
      className="w-full overflow-hidden rounded-2xl border"
      style={{ background: 'rgb(var(--surface))', borderColor: 'rgb(var(--line))', boxShadow: 'var(--shadow-md)' }}
    >
      <div className="border-b px-4 py-3" style={{ borderColor: 'rgb(var(--line-soft))' }}>
        <div className="text-[10px] uppercase tracking-[0.14em] text-faint">Amount</div>
        <div className="tabular mt-0.5 text-[26px] font-semibold leading-none tracking-tight text-ink">₹250.00</div>
      </div>
      <div className="grid grid-cols-2 gap-2 p-3">
        {[
          { icon: Banknote, label: 'Cash' },
          { icon: QrCode, label: 'UPI' },
          { icon: QrCode, label: 'Scan to pay' },
          { icon: CreditCard, label: 'Card' },
        ].map((m) => (
          <div
            key={m.label}
            className="flex items-center gap-2 rounded-xl border px-3 py-2.5"
            style={{ borderColor: 'rgb(var(--line))' }}
          >
            <m.icon className="h-3.5 w-3.5 shrink-0 text-ink-soft" aria-hidden="true" />
            <span className="truncate text-[12px] font-medium text-ink">{m.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
